import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Tag, Tabs, Drawer, InputNumber, Tooltip, Card, Empty, Row, Col,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';

import { selectFilter } from '../utils/selectFilter';
// Eval Insights: where to look, rather than every number.
//   Weakest Areas     sections and questions averaging under the threshold,
//                     lowest first, with the change vs the previous period
//   Struggling Agents agent x section grid coloured against the threshold, and
//                     the bottom agents for any section or question
//   Agent Check-up    one agent's sections and questions against the average
//                     for the same filters, below-threshold ones first
// Low scoring = average under the threshold (default 90%). Nothing is ranked
// or flagged on fewer answers than the minimum sample (default 3) -- one bad
// answer would otherwise top every list. Same filters and the same section /
// question figures as Eval Agent Stats, so the numbers agree.

const { RangePicker } = DatePicker;
const pct = (earned, max) => (max > 0 ? Math.round((earned / max) * 1000) / 10 : null);
const byNum = (k) => (a, b) => (a[k] ?? -1) - (b[k] ?? -1);

function sumUp(rows, keyOf, sampleField) {
  const m = {};
  rows.forEach((r) => {
    if (!(r.TotalMax > 0)) return;      // questions worth no points are not scored
    const k = keyOf(r);
    const g = (m[k] ||= { key: k, SectionName: r.SectionName, QuestionText: r.QuestionText, Earned: 0, Max: 0, Sample: 0, agents: [] });
    g.Earned += r.TotalEarned;
    g.Max += r.TotalMax;
    g.Sample += r[sampleField] || 0;
    g.agents.push(r);
  });
  return Object.values(m).map((g) => ({ ...g, Avg: pct(g.Earned, g.Max) }));
}

/**
 * `embedded` (Campaign Summary > Evaluations > Insights) renders this page
 * scoped to the campaign and call-date window passed in: no heading, no Call
 * Date or Campaign filters (Eval Form and Skill stay), and the Agent Check-up
 * agent is kept on the page rather than in the address, which is the
 * Campaign Summary's.
 */
export default function EvalInsightsPage({ embedded = false, campaignId = null, from = null, to = null, skills: groupSkills = null }) {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const params = embedded ? new URLSearchParams() : urlParams;
  const setParams = (p) => { if (!embedded) setUrlParams(p); };
  const [form] = Form.useForm();

  const [forms, setForms]         = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [skills, setSkills]       = useState([]);
  const [threshold, setThreshold] = useState(90);
  const [minSample, setMinSample] = useState(1);

  const [secRows, setSecRows]   = useState([]);
  const [qRows, setQRows]       = useState([]);
  const [prevSec, setPrevSec]   = useState([]);
  const [prevQ, setPrevQ]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [tab, setTab]           = useState(params.get('agent') ? 'agent' : 'weakest');
  const [agentId, setAgentId]   = useState(params.get('agent') ? Number(params.get('agent')) : null);
  const [qPick, setQPick]       = useState(null);
  const [panel, setPanel]       = useState(null);    // { title, rows, kind }

  useEffect(() => {
    api.get('/api/eval-forms/names').then((r) => setForms(r.data.map((n) => ({ label: n, value: n })))).catch(() => {});
    api.get('/api/agent-stats/options').then((r) => {
      setCampaigns(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
      setSkills(r.data.skills.map((s) => ({ label: s, value: s })));
    }).catch(() => {});
  }, []);

  const buildParams = (range) => {
    const v = form.getFieldsValue();
    const p = {};
    if (range?.[0]) p.call_date_start = range[0].format('YYYY-MM-DD');
    if (range?.[1]) p.call_date_end   = range[1].format('YYYY-MM-DD');
    if (v.form_names?.length) p.form_names = v.form_names.join(',');
    if (v.campaigns?.length)  p.campaigns  = v.campaigns.join(',');
    if (v.skills?.length)     p.skills     = v.skills.join(',');
    return p;
  };

  const load = useCallback(async () => {
    const range = form.getFieldValue('range');
    setLoading(true);
    try {
      const cur = buildParams(range);
      // The previous period of the same length, for the change column.
      let prevP = null;
      if (range?.[0] && range?.[1]) {
        const days = range[1].diff(range[0], 'day') + 1;
        prevP = buildParams([range[0].subtract(days, 'day'), range[0].subtract(1, 'day')]);
      }
      const reqs = [
        api.get('/api/eval-analytics/agent-sections', { params: { ...cur, group_by: 'summary' } }),
        api.get('/api/eval-analytics/agent-questions', { params: { ...cur, group_by: 'summary' } }),
        prevP ? api.get('/api/eval-analytics/agent-sections', { params: { ...prevP, group_by: 'summary' } }) : null,
        prevP ? api.get('/api/eval-analytics/agent-questions', { params: { ...prevP, group_by: 'summary' } }) : null,
      ];
      const [s, q, ps, pq] = await Promise.all(reqs.map((r) => (r ? r.catch(() => ({ data: [] })) : Promise.resolve({ data: [] }))));
      setSecRows(s.data || []); setQRows(q.data || []);
      setPrevSec(ps.data || []); setPrevQ(pq.data || []);
    } finally {
      setLoading(false);
    }
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  // Embedded: the Campaign Summary's campaign and window, and follow them.
  const scopeValues = () => ({
    range: from && to ? [dayjs(from), dayjs(to)] : [dayjs().startOf('month'), dayjs()],
    campaigns: campaignId ? [campaignId] : [],
    // The Campaign Summary's Skill Group fills the Skill filter (still editable).
    ...(embedded ? { skills: groupSkills || [] } : {}),
  });
  // On its own the page waits for a campaign before searching (every
  // campaign at once is no use); embedded, the campaign is already chosen.
  useEffect(() => {
    form.setFieldsValue(embedded ? scopeValues() : { range: [dayjs().startOf('month'), dayjs()] });
    if (embedded) load();
  }, []); // eslint-disable-line

  const firstScope = useRef(true);
  useEffect(() => {
    if (!embedded) return;
    if (firstScope.current) { firstScope.current = false; return; }
    form.setFieldsValue(scopeValues());
    load();
  }, [embedded, campaignId, from, to, groupSkills]); // eslint-disable-line

  // -- figures ---------------------------------------------------------------
  const sections  = useMemo(() => sumUp(secRows, (r) => r.SectionName, 'CaseCount'), [secRows]);
  const questions = useMemo(() => sumUp(qRows, (r) => `${r.SectionName}||${r.QuestionText}`, 'AnswerCount'), [qRows]);
  const prevSecAvg = useMemo(() => Object.fromEntries(sumUp(prevSec, (r) => r.SectionName, 'CaseCount').map((g) => [g.key, g.Avg])), [prevSec]);
  const prevQAvg   = useMemo(() => Object.fromEntries(sumUp(prevQ, (r) => `${r.SectionName}||${r.QuestionText}`, 'AnswerCount').map((g) => [g.key, g.Avg])), [prevQ]);

  const low = (avg, sample) => avg != null && sample >= minSample && avg < threshold;
  const agentLow = (r, sampleField) => low(pct(r.TotalEarned, r.TotalMax), r[sampleField] || 0);

  const weakSections = sections.filter((g) => low(g.Avg, g.Sample)).sort(byNum('Avg'))
    .map((g) => ({ ...g, Prev: prevSecAvg[g.key], AgentsBelow: g.agents.filter((r) => agentLow(r, 'CaseCount')).length }));
  const weakQuestions = questions.filter((g) => low(g.Avg, g.Sample)).sort(byNum('Avg'))
    .map((g) => ({ ...g, Prev: prevQAvg[g.key], AgentsBelow: g.agents.filter((r) => agentLow(r, 'AnswerCount')).length }));

  const agents = useMemo(() => {
    const m = {};
    [...secRows, ...qRows].forEach((r) => { if (r.AgentUserID) m[r.AgentUserID] = r.AgentName; });
    return Object.entries(m).map(([id, name]) => ({ value: Number(id), label: name })).sort((a, b) => a.label.localeCompare(b.label));
  }, [secRows, qRows]);

  // -- shared bits -------------------------------------------------------------
  const scoreTag = (avg, sample) => {
    if (avg == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
    if (sample < minSample) return <Tooltip title={`Only ${sample} — under the minimum sample`}><span style={{ color: '#bfbfbf' }}>{avg}%</span></Tooltip>;
    return <Tag color={avg < threshold ? 'red' : 'green'} style={{ marginRight: 0 }}>{avg}%</Tag>;
  };
  const change = (cur, prev) => {
    if (cur == null || prev == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
    const d = Math.round((cur - prev) * 10) / 10;
    if (d === 0) return <span style={{ color: '#8c8c8c' }}>0</span>;
    return d > 0
      ? <span style={{ color: '#52c41a' }}><ArrowUpOutlined /> {d}</span>
      : <span style={{ color: '#ff4d4f' }}><ArrowDownOutlined /> {Math.abs(d)}</span>;
  };

  // Agents on a section or question, lowest first.
  const openAgents = (title, rows, sampleField) => setPanel({
    title, kind: 'agents',
    rows: rows.map((r) => ({ ...r, Avg: pct(r.TotalEarned, r.TotalMax), Sample: r[sampleField] || 0 }))
      .sort((a, b) => (a.Sample >= minSample ? 0 : 1) - (b.Sample >= minSample ? 0 : 1) || (a.Avg ?? 999) - (b.Avg ?? 999)),
  });

  // The evaluations that lost points on one agent's section (the feed
  // behind Section History only returns those -- what pulled the score down).
  const openEvals = async (agent, section) => {
    setPanel({ title: `${agent.AgentName}: ${section} (evaluations that lost points)`, kind: 'evals', rows: [], loading: true });
    try {
      const res = await api.get('/api/eval-analytics/agent-sections',
        { params: { ...buildParams(form.getFieldValue('range')), group_by: 'section_case' } });
      const rows = (res.data || []).filter((r) => r.AgentUserID === agent.AgentUserID && r.SectionName === section)
        .map((r) => ({ ...r, Avg: r.ScorePct ?? pct(r.Earned, r.MaxPts) }));
      setPanel({ title: `${agent.AgentName}: ${section} (evaluations that lost points)`, kind: 'evals', rows });
    } catch {
      setPanel({ title: `${agent.AgentName}: ${section} (evaluations that lost points)`, kind: 'evals', rows: [] });
    }
  };

  const openCheckup = (id) => {
    setAgentId(id);
    setTab('agent');
    setPanel(null);
    setParams({ agent: String(id) });
  };

  // -- tab 1: weakest areas ----------------------------------------------------
  const weakestTab = (
    <Row gutter={16}>
      <Col xs={24} xl={10}>
        <Card size="small" title={`Bottom Sections (under ${threshold}%)`} style={{ marginBottom: 16 }}>
          <Table rowKey="key" size="small" loading={loading} dataSource={weakSections} pagination={false}
            locale={{ emptyText: `No section averages under ${threshold}%.` }}
            onRow={(g) => ({ onClick: () => openAgents(`Agents on ${g.SectionName}`, g.agents, 'CaseCount'), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', ellipsis: true },
              { title: 'Avg', key: 'Avg', width: 90, align: 'center', render: (_, g) => scoreTag(g.Avg, g.Sample) },
              { title: 'Evals', dataIndex: 'Sample', key: 'Sample', width: 70, align: 'right' },
              { title: 'Change', key: 'chg', width: 80, align: 'right', render: (_, g) => change(g.Avg, g.Prev) },
              { title: 'Agents Below', dataIndex: 'AgentsBelow', key: 'AgentsBelow', width: 110, align: 'right' },
            ]} />
        </Card>
      </Col>
      <Col xs={24} xl={14}>
        <Card size="small" title={`Bottom Questions (under ${threshold}%)`} style={{ marginBottom: 16 }}>
          <Table rowKey="key" size="small" loading={loading} dataSource={weakQuestions}
            pagination={{ defaultPageSize: 15, hideOnSinglePage: true }}
            locale={{ emptyText: `No question averages under ${threshold}%.` }}
            onRow={(g) => ({ onClick: () => openAgents(`Agents on ${g.QuestionText}`, g.agents, 'AnswerCount'), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Question', dataIndex: 'QuestionText', key: 'QuestionText',
                onCell: () => ({ style: { whiteSpace: 'normal' } }),
                render: (v, g) => <div><div>{v}</div><div style={{ fontSize: 12, color: '#8c8c8c' }}>{g.SectionName}</div></div> },
              { title: 'Avg', key: 'Avg', width: 90, align: 'center', render: (_, g) => scoreTag(g.Avg, g.Sample) },
              { title: 'Answers', dataIndex: 'Sample', key: 'Sample', width: 80, align: 'right' },
              { title: 'Change', key: 'chg', width: 80, align: 'right', render: (_, g) => change(g.Avg, g.Prev) },
              { title: 'Agents Below', dataIndex: 'AgentsBelow', key: 'AgentsBelow', width: 110, align: 'right' },
            ]} />
        </Card>
      </Col>
    </Row>
  );

  // -- tab 2: struggling agents ------------------------------------------------
  const secNames = sections.map((g) => g.key).sort();
  const grid = useMemo(() => {
    const m = {};
    secRows.forEach((r) => {
      if (!(r.TotalMax > 0)) return;
      const k = r.AgentUserID ?? `name:${r.AgentName}`;
      const a = (m[k] ||= { key: k, AgentUserID: r.AgentUserID, AgentName: r.AgentName, cells: {}, lowCount: 0 });
      a.cells[r.SectionName] = r;
    });
    return Object.values(m);
  }, [secRows]);
  const gridRows = grid.map((a) => ({
    ...a,
    lowCount: Object.values(a.cells).filter((r) => agentLow(r, 'CaseCount')).length,
  })).sort((a, b) => b.lowCount - a.lowCount || a.AgentName.localeCompare(b.AgentName));

  const qOptions = questions.map((g) => ({ value: g.key, label: `${g.QuestionText} (${g.SectionName})` }));
  const pickedQ = questions.find((g) => g.key === qPick);

  const strugglingTab = (
    <>
      <Card size="small" title={`Agents by Section (red = under ${threshold}%)`} style={{ marginBottom: 16 }}>
        <Table rowKey="key" size="small" loading={loading} dataSource={gridRows} pagination={false} bordered
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No evaluations for these filters.' }}
          columns={[
            { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', fixed: 'left', width: 190,
              sorter: (a, b) => a.AgentName.localeCompare(b.AgentName),
              render: (v, a) => (a.AgentUserID
                ? <Typography.Link onClick={() => openCheckup(a.AgentUserID)}>{v}</Typography.Link> : v) },
            { title: 'Sections Below', dataIndex: 'lowCount', key: 'lowCount', width: 120, align: 'right',
              sorter: byNum('lowCount'), defaultSortOrder: 'descend' },
            ...secNames.map((sec) => ({
              title: sec, key: sec, width: 120, align: 'center',
              sorter: (a, b) => (pct(a.cells[sec]?.TotalEarned, a.cells[sec]?.TotalMax) ?? 999) - (pct(b.cells[sec]?.TotalEarned, b.cells[sec]?.TotalMax) ?? 999),
              render: (_, a) => {
                const r = a.cells[sec];
                if (!r) return <span style={{ color: '#d9d9d9' }}>-</span>;
                return (
                  <span style={{ cursor: 'pointer' }} onClick={() => openEvals(a, sec)}>
                    {scoreTag(pct(r.TotalEarned, r.TotalMax), r.CaseCount || 0)}
                  </span>
                );
              },
            })),
          ]} />
      </Card>
      <Card size="small" title="Bottom Agents on a Question">
        <Select showSearch allowClear optionFilterProp="label" placeholder="Pick a question" style={{ width: '100%', maxWidth: 700, marginBottom: 12 }}
          value={qPick} onChange={setQPick} options={qOptions} />
        {pickedQ ? (
          <Table rowKey={(r) => r.AgentUserID ?? r.AgentName} size="small" pagination={false}
            dataSource={pickedQ.agents.map((r) => ({ ...r, Avg: pct(r.TotalEarned, r.TotalMax), Sample: r.AnswerCount || 0 }))
              .filter((r) => r.Sample >= minSample).sort(byNum('Avg')).slice(0, 10)}
            locale={{ emptyText: `No agent has ${minSample}+ answers on this question.` }}
            columns={[
              { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName',
                render: (v, r) => (r.AgentUserID ? <Typography.Link onClick={() => openCheckup(r.AgentUserID)}>{v}</Typography.Link> : v) },
              { title: 'Avg', key: 'Avg', width: 90, align: 'center', render: (_, r) => scoreTag(r.Avg, r.Sample) },
              { title: 'Answers', dataIndex: 'Sample', key: 'Sample', width: 90, align: 'right' },
            ]} />
        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pick a question to see its bottom 10 agents." />}
      </Card>
    </>
  );

  // -- tab 3: agent check-up ---------------------------------------------------
  const checkRows = (rows, groups, sampleField, keyOf) => rows
    .filter((r) => r.AgentUserID === agentId && r.TotalMax > 0)
    .map((r) => {
      const avg = pct(r.TotalEarned, r.TotalMax);
      const all = groups.find((g) => g.key === keyOf(r));
      const sample = r[sampleField] || 0;
      return { ...r, key: keyOf(r), Avg: avg, Sample: sample, AllAvg: all?.Avg ?? null,
               Gap: avg != null && all?.Avg != null ? Math.round((avg - all.Avg) * 10) / 10 : null,
               Below: low(avg, sample) };
    })
    .sort((a, b) => (b.Below - a.Below) || ((a.Gap ?? 0) - (b.Gap ?? 0)));
  const agentSecs = agentId ? checkRows(secRows, sections, 'CaseCount', (r) => r.SectionName) : [];
  const agentQs   = agentId ? checkRows(qRows, questions, 'AnswerCount', (r) => `${r.SectionName}||${r.QuestionText}`) : [];
  const gapCell = (g) => (g == null ? '—' : <span style={{ color: g < 0 ? '#ff4d4f' : '#52c41a' }}>{g > 0 ? '+' : ''}{g}</span>);

  const checkupTab = (
    <>
      <Select showSearch allowClear optionFilterProp="label" placeholder="Pick an agent" style={{ width: 320, marginBottom: 16 }}
        value={agentId} onChange={(v) => { setAgentId(v ?? null); setParams(v ? { agent: String(v) } : {}); }} options={agents} />
      {!agentId ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pick an agent." /> : (
        <Row gutter={16}>
          <Col xs={24} xl={10}>
            <Card size="small" title="Sections" style={{ marginBottom: 16 }}>
              <Table rowKey="key" size="small" pagination={false} dataSource={agentSecs}
                onRow={(r) => ({ onClick: () => openEvals(r, r.SectionName), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', ellipsis: true },
                  { title: 'Agent', key: 'Avg', width: 90, align: 'center', render: (_, r) => scoreTag(r.Avg, r.Sample) },
                  { title: 'Average', key: 'AllAvg', width: 80, align: 'right', render: (_, r) => (r.AllAvg != null ? `${r.AllAvg}%` : '—') },
                  { title: 'Gap', key: 'Gap', width: 70, align: 'right', render: (_, r) => gapCell(r.Gap) },
                  { title: 'Evals', dataIndex: 'Sample', key: 'Sample', width: 60, align: 'right' },
                ]} />
            </Card>
          </Col>
          <Col xs={24} xl={14}>
            <Card size="small" title="Questions" style={{ marginBottom: 16 }}>
              <Table rowKey="key" size="small" dataSource={agentQs} pagination={{ defaultPageSize: 15, hideOnSinglePage: true }}
                columns={[
                  { title: 'Question', dataIndex: 'QuestionText', key: 'QuestionText',
                    onCell: () => ({ style: { whiteSpace: 'normal' } }),
                    render: (v, r) => <div><div>{v}</div><div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.SectionName}</div></div> },
                  { title: 'Agent', key: 'Avg', width: 90, align: 'center', render: (_, r) => scoreTag(r.Avg, r.Sample) },
                  { title: 'Average', key: 'AllAvg', width: 80, align: 'right', render: (_, r) => (r.AllAvg != null ? `${r.AllAvg}%` : '—') },
                  { title: 'Gap', key: 'Gap', width: 70, align: 'right', render: (_, r) => gapCell(r.Gap) },
                  { title: 'Answers', dataIndex: 'Sample', key: 'Sample', width: 75, align: 'right' },
                ]} />
            </Card>
          </Col>
        </Row>
      )}
    </>
  );

  return (
    <div>
      {!embedded && <Typography.Title level={4} style={{ marginTop: 0 }}>Eval Insights</Typography.Title>}

      <Form form={form} layout="inline" onFinish={load} style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Form.Item name="range" label="Call Date" hidden={embedded}><RangePicker style={{ width: 260 }} allowClear={false} /></Form.Item>
        <Form.Item name="campaigns" label="Campaign" hidden={embedded}
          rules={embedded ? [] : [{ required: true, message: 'Select a campaign' }]}>
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" maxTagCount="responsive" placeholder="All campaigns" style={{ width: 220 }} options={campaigns} />
        </Form.Item>
        <Form.Item name="form_names" label="Eval Form">
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="All forms" style={{ width: 220 }} options={forms} />
        </Form.Item>
        <Form.Item name="skills" label="Skill">
          <Select mode="multiple" allowClear showSearch filterOption={selectFilter} maxTagCount="responsive" placeholder="All skills" style={{ width: 200 }} options={skills} />
        </Form.Item>
        <Form.Item><Button type="primary" htmlType="submit" loading={loading}>Filter</Button></Form.Item>
      </Form>

      <Space wrap style={{ marginBottom: 8 }}>
        <span>Low score under:</span>
        <InputNumber min={0} max={100} value={threshold} onChange={(v) => setThreshold(v ?? 90)} addonAfter="%" style={{ width: 110 }} />
        <span style={{ marginLeft: 12 }}>Minimum sample:</span>
        <InputNumber min={1} max={100} value={minSample} onChange={(v) => setMinSample(v ?? 1)} style={{ width: 80 }} />
      </Space>

      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: 'weakest',    label: 'Weakest Areas',     children: weakestTab },
        { key: 'struggling', label: 'Struggling Agents', children: strugglingTab },
        { key: 'agent',      label: 'Agent Check-up',    children: checkupTab },
      ]} />

      <Drawer title={panel?.title} open={!!panel} onClose={() => setPanel(null)} width={760} destroyOnClose>
        {panel?.kind === 'agents' ? (
          <Table rowKey={(r) => r.AgentUserID ?? r.AgentName} size="small" pagination={false} dataSource={panel.rows}
            columns={[
              { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName',
                render: (v, r) => (r.AgentUserID ? <Typography.Link onClick={() => openCheckup(r.AgentUserID)}>{v}</Typography.Link> : v) },
              { title: 'Avg', key: 'Avg', width: 90, align: 'center', render: (_, r) => scoreTag(r.Avg, r.Sample) },
              { title: 'Sample', dataIndex: 'Sample', key: 'Sample', width: 80, align: 'right' },
            ]} />
        ) : (
          <Table rowKey={(r) => `${r.CaseID}-${r.SectionName}`} size="small" loading={panel?.loading} pagination={false}
            dataSource={panel?.rows || []}
            locale={{ emptyText: 'No evaluations lost points on this section.' }}
            onRow={(r) => ({ onClick: () => navigate(`/cases/${r.CaseID}`), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Eval ID', dataIndex: 'CaseID', key: 'CaseID', width: 90 },
              { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 110, render: (v) => (v || '').slice(0, 10) },
              { title: 'Section Score', key: 'Avg', width: 120, align: 'center', render: (_, r) => scoreTag(r.Avg, minSample) },
              { title: 'Points', key: 'pts', width: 100, align: 'right', render: (_, r) => `${r.Earned} / ${r.MaxPts}` },
            ]} />
        )}
      </Drawer>
    </div>
  );
}
